// stardew-avatar.ts — Standalone farmer avatar canvas compositor
// Reads .sv-equip-canvas[data-appearance] JSON, loads per-layer CDN sprites,
// applies Stardew's real palette-index tinting, and renders a layered walk-in-place animation.

// ── Constants ─────────────────────────────────────────────────────────────────

const CDN = 'https://stats.tor1.cdn.digitaloceanspaces.com/sv/farmer/';
const WALK_CYCLE = [0, 1, 2, 1] as const;   // 4-step cycle over the body walk frames
const FRAME_MS = 200;                         // ms per walk cycle step

// All head-attached layers (shirt/hat/accessory/hair) use the canonical SDV
// FEATURE_Y_OFFSET_PER_FRAME table for per-frame head bob. The prior HEAD_BOB
// shortcut [0,1,1,1] has been removed — it rendered hair 1 src px too high
// relative to canonical SDV (FarmerRenderer.cs:846 down-facing hair Y).
const SCALE = 4;                              // 4× upscale for pixel-art crispness

// SDV FarmerRenderer.cs featureYOffsetPerFrame — per-walk-frame Y bob in SOURCE pixels.
// Applies to shirt, hat, and accessory layers (face/torso group that bobs with the
// walk cycle). 126 entries cover the full per-frame animation set; we index by the
// body walk frame (0..2 for the 3 down-facing walk frames the avatar uses).
const FEATURE_Y_OFFSET_PER_FRAME: readonly number[] = [
     1,  2,  2,  0,  5,  6,  1,  2,  2,  1,  0,  2,  0,  1,  1,  0,  2,  2,  3,  3,
     2,  2,  1,  1,  0,  0,  2,  2,  4,  4,  0,  0,  1,  2,  1,  1,  1,  1,  0,  0,
     1,  1,  1,  0,  0, -2, -1,  1,  1,  0, -1, -2, -1, -1,  5,  4,  0,  0,  3,  2,
    -1,  0,  4,  2,  0,  0,  2,  1,  0, -1,  1, -2,  0,  0,  1,  1,  1,  1,  1,  1,
     0,  0,  0,  0,  1, -1, -1, -1, -1,  1,  1,  0,  0,  0,  0,  4,  1,  0,  1,  2,
     1,  0,  1,  0,  1,  2, -3, -4, -1,  0,  0,  2,  1, -4, -1,  0,  0, -3,  0,  0,
    -1,  0,  0,  2,  1,  1,
];

// Canvas is 136 tall = 34 source px × 4 = body(32) + 2 source px hat headroom.
// Body renders at canvas y=8 so its 128px height fills exactly to the bottom edge.
const BODY_Y = 8;             // canvas pixels from top
const BODY_Y_SRC = 2;         // source pixels (BODY_Y / SCALE)

// Layer draw offsets (source pixel scale, relative to body origin)
const SHIRT_X_SRC = 4;             // source pixels right of body x=0
const SHIRT_Y_BASE_SRC = 14;       // SDV FarmerRenderer: shirt y = 14 + featureYOffsetPerFrame[bodyFrame]
const HAT_X_SRC = -2;              // source pixels (20px hat centred over 16px body)
const HAT_Y_BASE_SRC = -3;         // SDV FarmerRenderer: hat y from body top = -3 + featureYOffsetPerFrame[bodyFrame]
                                    // (TODO: hairstyleHatOffset[hair%16] table from FarmerRenderer is deferred per RESEARCH)
// Pants Y audit (Phase 78-02): pants Y is unchanged. SDV draws pants at the same
// body origin and the body walk frames already encode the leg animation — no
// featureYOffsetPerFrame add needed for the pants layer.

// Hairs whose dome extends into the hat-brim zone (topmost opaque pixel at source
// y < 3 in the down-facing 16x32 frame). Derived from a one-time pixel inspection
// of Characters/Farmer/hairstyles.png + hairstyles2.png. Only these hairs get the
// y=0..7 top-crop when a hat is equipped — for all others, drawing the full hair
// leaves no skin gap because their hairline starts at or below the brim line.
// See Phase 78-05 SUMMARY for the full top_y table.
const HAIRS_NEEDING_HAT_CROP: ReadonlySet<number> = new Set([
    0, 1, 2, 3, 4, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
    19, 21, 22, 35, 37, 38, 40, 42, 49, 60, 62, 68, 70,
]);

// ── Sentinel / palette constants ──────────────────────────────────────────────

// Skin sentinels (verified from vanilla farmer_base.png, skin_index=0)
const SKIN_SENTINELS = [
    [107, 0,   58 ] as [number, number, number],   // dark   — col 0 of skinColors row
    [224, 107, 101] as [number, number, number],   // mid    — col 1 of skinColors row
    [249, 174, 137] as [number, number, number],   // light  — col 2 of skinColors row
] as const;

// Eye sentinels (verified from vanilla farmer_base.png)
const EYE_SENTINEL_LIGHT = [190, 168, 168] as const;  // → newEyeColor
const EYE_SENTINEL_DARK  = [104, 43,  15 ] as const;  // → changeBrightness(newEyeColor, -75)

// Shoe sentinels — the 4 boot colors baked into farmer_base/farmer_girl_base.png.
// FarmerRenderer.ApplyShoeColor reads them from palette pixels x=268..271,y=0 (the
// `shoeDarkestColorIndex = 268` constant) and swaps each for a row of shoeColors.png.
// Order is dark→light, matching the 4 columns of a BOOT_COLOR_ROWS entry.
const SHOE_SENTINELS = [
    [61,  17, 35] as [number, number, number],   // darkest
    [91,  31, 36] as [number, number, number],   // medium
    [119, 41, 26] as [number, number, number],   // lightest3
    [173, 71, 27] as [number, number, number],   // lightest2
] as const;

// ── Hat lookup table (28 string-keyed entries from hats.json) ─────────────────

const HAT_STRING_SPRITE_INDEX: Record<string, number> = {
    'AbigailsBow':     94,
    'TricornHat':      95,
    'JojaCap':         96,
    'LaurelWreathCrown': 97,
    'GilsHat':         98,
    'BlueBow':         99,
    'DarkVelvetBow':   100,
    'MummyMask':       101,
    'BucketHat':       102,
    'SquidHat':        103,
    'SportsCap':       104,
    'RedFez':          105,
    'RaccoonHat':      106,
    'SteelPanHat':     107,
    'GoldPanHat':      108,
    'IridiumPanHat':   109,
    'MysteryHat':      110,
    'DarkBallcap':     111,
    'LeprechuanHat':   112,
    'JunimoHat':       113,
    'PaperHat':        114,
    'PageboyCap':      115,
    'JesterHat':       116,
    'BlueRibbon':      117,
    'GovernorsHat':    118,
    'WhiteBow':        119,
    'SpaceHelmet':     120,
    'InfinityCrown':   121,
};

// Pants items 998 and 999 are aliases to SpriteIndex 0 and 8 respectively
const PANTS_ALIASES: Record<number, number> = { 998: 0, 999: 8 };

// ── Hairstyle → hat Y offset lookup ───────────────────────────────────────────
// SDV FarmerRenderer.hairstyleHatOffset — per-hairstyle vertical push applied
// to the HAT Y. Values sourced verbatim from decompiled FarmerRenderer.cs
// (veywrn/StardewValley) `public static int[] hairstyleHatOffset` (line 301).
// Indexed by hair_index % 16. Only indices 3 (4), 6 (3), 8 (4) are non-zero.
//
// CRITICAL — UNITS: in SDV the hat draw call (FarmerRenderer.cs line 959) is:
//   new Vector2(..., -16 + featureYOffsetPerFrame[currentFrame] * 4
//                          + ((!hat.ignoreHairstyleOffset) ? hairstyleHatOffset[hair%16] : 0)
//                          + 4 + heightOffset)
// Note that `featureYOffsetPerFrame[...] * 4` is multiplied by 4 (sprite scale)
// to convert source-px → screen-px, but `hairstyleHatOffset` is NOT multiplied.
// That means SDV applies it as raw DESTINATION pixels (1/4 of a source pixel).
// 4 dest px = 1 source px. So the canonical push for hair 3/8 is exactly
// 1 source pixel down; for hair 6 it's 0.75 source px (rounds to ~1 src px).
//
// In our renderer we store source-pixel values, so we DIVIDE by SCALE when
// applying. Prior (Phase 78.1 v1) applied the raw table as source pixels —
// 4× too far down, landing the cowboy hat on the neck.
//
// Phase 78.1 replaces the prior hat-hides-hair gate from Phase 78-01. The
// canonical SDV `FarmerRenderer.drawHairAndAccesories` never skips, clips, or
// shifts the hair to occlude under a hat — it relies on the hat sprite z-order
// and on shifting the HAT down by hairstyleHatOffset to clear taller tufts.
const HAIRSTYLE_HAT_OFFSET_DEST_PX: readonly number[] = [
    0, 0, 0, 4, 0, 0, 3, 0, 4, 0, 0, 0, 0, 0, 0, 0,
] as const;

// ── Boots: shoe-pixel recolor table ───────────────────────────────────────────

// shoeColors.png (Characters/Farmer/shoeColors, 4×19) inlined as a constant table.
// Each row is one boot ColorIndex; the 4 RGB triples map 1:1 onto SHOE_SENTINELS
// (dark→light). Inlined rather than CDN-loaded — it is immutable vanilla data and
// far smaller than an extra image fetch + CORS dependency.
const BOOT_COLOR_ROWS: ReadonlyArray<ReadonlyArray<readonly [number, number, number]>> = [
    [[85, 0, 0],    [125, 0, 0],     [170, 0, 0],     [212, 0, 0]],       // 0
    [[3, 50, 43],   [67, 89, 37],    [95, 124, 50],   [136, 178, 77]],    // 1
    [[67, 22, 0],   [105, 44, 0],    [142, 66, 0],    [179, 85, 0]],      // 2
    [[35, 0, 0],    [87, 58, 11],    [123, 85, 26],   [163, 118, 42]],    // 3
    [[15, 20, 47],  [65, 70, 81],    [109, 116, 131], [150, 162, 162]],   // 4
    [[41, 25, 32],  [88, 58, 54],    [152, 105, 96],  [181, 125, 116]],   // 5
    [[0, 53, 57],   [25, 92, 98],    [0, 147, 167],   [0, 206, 206]],     // 6
    [[0, 0, 0],     [38, 38, 38],    [54, 54, 54],    [66, 66, 66]],      // 7
    [[17, 13, 8],   [51, 7, 2],      [77, 0, 0],      [103, 0, 0]],       // 8
    [[46, 0, 74],   [107, 0, 151],   [140, 0, 175],   [188, 0, 235]],     // 9
    [[15, 20, 47],  [54, 0, 101],    [75, 1, 151],    [99, 1, 229]],      // 10
    [[35, 0, 0],    [83, 28, 0],     [142, 66, 0],    [179, 85, 0]],      // 11
    [[33, 11, 5],   [63, 21, 28],    [91, 32, 16],    [119, 47, 21]],     // 12
    [[0, 0, 51],    [18, 28, 107],   [0, 56, 145],    [11, 99, 180]],     // 13
    [[0, 30, 42],   [0, 62, 65],     [14, 93, 58],    [50, 133, 48]],     // 14
    [[16, 10, 9],   [44, 6, 17],     [65, 4, 28],     [113, 7, 39]],      // 15
    [[14, 14, 51],  [30, 38, 104],   [0, 56, 145],    [9, 101, 130]],     // 16
    [[0, 21, 58],   [40, 37, 107],   [92, 74, 147],   [170, 129, 196]],   // 17
    [[33, 33, 33],  [145, 140, 127], [219, 215, 206], [242, 242, 242]],   // 18
];

// Boots clothing item ID → ColorIndex into BOOT_COLOR_ROWS.
// ColorIndex is field 5 of each Data/Boots.json entry; index 12 is unused by any boot.
const BOOT_COLOR_INDEX: Record<number, number> = {
    504: 0,  505: 1,  506: 2,  507: 3,  508: 4,  509: 5,  510: 6,
    511: 7,  512: 8,  513: 9,  514: 10, 515: 11, 804: 13, 806: 14,
    853: 15, 854: 16, 855: 17, 878: 18,
};

// Shirt clothing item IDs map to shirts.png SpriteIndex as (itemId - 1000),
// except for these 13 late-range shirts (from Data/Shirts.json).
const SHIRT_SPRITE_EXCEPTIONS: Record<number, number> = {
    1288: 290, 1289: 291, 1290: 292, 1291: 293, 1292: 294,
    1293: 295, 1294: 296, 1295: 297, 1296: 298, 1297: 299,
    1997: 272, 1998: 289, 1999: 288,
};

// Phase 78-03 Bug 3: Dyeable shirt SpriteIndex set (55 entries, derived from
// Data/Shirts.json CanBeDyed=true). Only these shirts have right-half dye masks
// and need MultiplyColor compositing with shirt_color. Non-dyeable shirts render
// the colored left half as-is (no tint).
const SHIRT_DYEABLE: ReadonlySet<number> = new Set<number>([
    123, 128, 129, 130, 131, 132, 133, 134, 136, 137, 139, 140,
    141, 142, 152, 153, 154, 158, 159, 166, 173, 174, 175, 176,
    177, 178, 191, 194, 196, 200, 209, 210, 216, 217, 218, 220,
    222, 224, 248, 259, 261, 262, 263, 270, 271, 272, 273, 277,
    278, 280, 281, 283, 284, 299, 301,
]);

// ── Interfaces ────────────────────────────────────────────────────────────────

interface RGB {
    r: number;
    g: number;
    b: number;
}

// Fields exactly as emitted by farm.latte data-appearance JSON (lines 493-509)
interface FarmerAppearance {
    hair_index: number;
    skin_index: number;
    accessory_index: number | null;
    shirt_index: number;
    is_male: boolean;
    hair_color: RGB;
    pants_color: RGB;
    shirt_color: RGB;       // Phase 78: clothesColor dye RGB; 255/255/255 = neutral (no tint)
    eye_color: RGB;
    hat_item_id: string | null;
    shirt_item_id: string | null;
    pants_item_id: string | null;
    boots_item_id: string | null;
    left_ring_item_id: string | null;
    right_ring_item_id: string | null;
    trinket_item_id: string | null;
}

// ── ID-resolution helpers ─────────────────────────────────────────────────────

function hatSpriteIndex(itemId: string): number {
    // itemId = '(H)42' or '(H)AbigailsBow'
    const key = itemId.replace(/^\(H\)/, '');
    if (/^\d+$/.test(key)) return parseInt(key, 10);
    return HAT_STRING_SPRITE_INDEX[key] ?? 0;
}

function pantsSpriteIndex(itemId: string | null): number {
    if (!itemId) return 0;
    const key = itemId.replace(/^\(P\)/, '');
    const n = parseInt(key, 10);
    if (isNaN(n)) return 0;
    return PANTS_ALIASES[n] ?? n;
}

// The farmer's modern shirtItem (e.g. '(C)1099') is authoritative. The legacy
// shirt_index is 0 whenever a shirtItem is equipped, so it is only a fallback.
function shirtSpriteIndex(itemId: string | null, fallback: number): number {
    if (!itemId) return fallback;
    const n = parseInt(itemId.replace(/^\([A-Z]+\)/, ''), 10);
    if (isNaN(n)) return fallback;
    return SHIRT_SPRITE_EXCEPTIONS[n] ?? (n - 1000);
}

// Resolve an equipped boots item (e.g. '(B)514') to its 4-shade shoe palette.
// Returns null when no boots are worn or the ID is unrecognised — callers then
// leave the brown shoes baked into the base sprite untouched.
function bootColorRow(itemId: string | null): readonly (readonly [number, number, number])[] | null {
    if (!itemId) return null;
    const n = parseInt(itemId.replace(/^\([A-Z]+\)/, ''), 10);
    if (isNaN(n)) return null;
    const colorIndex = BOOT_COLOR_INDEX[n];
    if (colorIndex === undefined) return null;
    return BOOT_COLOR_ROWS[colorIndex] ?? null;
}

// ── Module-level URL → Promise<Image> cache (HEAD-03) ─────────────────────────
// Memoizes loadImage() so concurrent first-callers of the same URL share one
// in-flight fetch (dogpile guard). Mirrors the skinColorsCachePromise pattern
// at lines below. Critical for the leaderboard head-strip render where ~24
// farmer heads compose from the same body/arm/hair sub-sprites.
const imageCache = new Map<string, Promise<HTMLImageElement>>();

// ── CORS-safe image loader ─────────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement> {
    const cached = imageCache.get(src);
    if (cached) return cached;
    const p = new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';      // MUST be set before src
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load: ${src}`));
        img.src = src;
    });
    imageCache.set(src, p);
    return p;
}

// ── URL builders ──────────────────────────────────────────────────────────────

function urlBodyFrame(gender: 'male' | 'female', frame: number): string {
    return `${CDN}body/${gender}/${frame}.png`;
}

function urlArmFrame(gender: 'male' | 'female', frame: number): string {
    return `${CDN}body/${gender}/arm/${frame}.png`;
}

function urlHairFrame(index: number, frame: number): string {
    return `${CDN}hair/${index}/${frame}.png`;
}

function urlShirt(spriteIndex: number): string {
    return `${CDN}shirts/${spriteIndex}.png`;
}

// Phase 78-03: dye mask URL for dyeable shirts. Only emitted to CDN for
// SpriteIndex values in SHIRT_DYEABLE (see slice-farmer.py do_shirts()).
function urlShirtMask(spriteIndex: number): string {
    return `${CDN}shirts/${spriteIndex}_mask.png`;
}

function urlAccessory(index: number): string {
    return `${CDN}accessories/${index}.png`;
}

function urlHat(itemId: string): string {
    return `${CDN}hats/${hatSpriteIndex(itemId)}.png`;
}

function urlPantsFrame(spriteIndex: number, frame: number): string {
    return `${CDN}pants/${spriteIndex}/${frame}.png`;
}

function urlSkinColors(): string {
    return `${CDN}skin-colors.png`;
}

// ── Pixel tinting functions ────────────────────────────────────────────────────

// Skin tinting: 3-sentinel palette swap from skinColors.png
// skinSentinels: 3 source RGB triples in farmer_base.png (dark/mid/light)
// skinColors: 3 replacement RGB triples from skinColors[skin_index] row
function applySkintone(
    imageData: ImageData,
    skinSentinels: ReadonlyArray<readonly [number, number, number]>,
    skinColors: [number, number, number][],
): void {
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        for (let s = 0; s < 3; s++) {
            const sen = skinSentinels[s];
            if (data[i] === sen[0] && data[i + 1] === sen[1] && data[i + 2] === sen[2]) {
                data[i]     = skinColors[s][0];
                data[i + 1] = skinColors[s][1];
                data[i + 2] = skinColors[s][2];
                break;
            }
        }
    }
}

// Eye tinting: 2-sentinel direct swap
// Light sentinel (190,168,168) → newEyeColor
// Dark sentinel (104,43,15)    → changeBrightness(newEyeColor, -75) = max(0, c-75) per channel
function applyEyeColor(imageData: ImageData, er: number, eg: number, eb: number): void {
    const darkR = Math.max(0, er - 75);
    const darkG = Math.max(0, eg - 75);
    const darkB = Math.max(0, eb - 75);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        if (data[i] === EYE_SENTINEL_LIGHT[0] && data[i + 1] === EYE_SENTINEL_LIGHT[1] && data[i + 2] === EYE_SENTINEL_LIGHT[2]) {
            data[i] = er; data[i + 1] = eg; data[i + 2] = eb;
        } else if (data[i] === EYE_SENTINEL_DARK[0] && data[i + 1] === EYE_SENTINEL_DARK[1] && data[i + 2] === EYE_SENTINEL_DARK[2]) {
            data[i] = darkR; data[i + 1] = darkG; data[i + 2] = darkB;
        }
    }
}

// Hair tinting: greyscale-detect then multiply blend
// Greyscale pixels (r === g === b, alpha > 0): output = (grey * hair / 255) per channel
// Non-greyscale (e.g. dark outline (60,13,35)) are left unchanged
function applyHairColor(imageData: ImageData, hr: number, hg: number, hb: number): void {
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 0 && data[i] === data[i + 1] && data[i + 1] === data[i + 2]) {
            const grey = data[i];
            data[i]     = (grey * hr / 255) | 0;
            data[i + 1] = (grey * hg / 255) | 0;
            data[i + 2] = (grey * hb / 255) | 0;
        }
    }
}

// Pants tinting: multiply every non-transparent pixel by the pants color
// output = (sprite.r * pants_r / 255, ...) per channel
function applyPantsColor(imageData: ImageData, pr: number, pg: number, pb: number): void {
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 0) {
            data[i]     = (data[i]     * pr / 255) | 0;
            data[i + 1] = (data[i + 1] * pg / 255) | 0;
            data[i + 2] = (data[i + 2] * pb / 255) | 0;
        }
    }
}

// Dye mask tinting (Phase 78-03 Bug 3): SDV FarmerRenderer dye-mask compositing —
// MultiplyColor (a.X * b.X / 255) per channel applied to EVERY non-transparent
// pixel of the mask sprite. Unlike applyHairColor, this MUST NOT greyscale-detect:
// mask pixels are near-neutral (R≈G≈B) but rarely R=G=B exactly (e.g. (71,68,57)
// would be skipped by the greyscale check — RESEARCH Pitfall 1).
function applyDyeMask(imageData: ImageData, r: number, g: number, b: number): void {
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        data[i]     = (data[i]     * r / 255) | 0;
        data[i + 1] = (data[i + 1] * g / 255) | 0;
        data[i + 2] = (data[i + 2] * b / 255) | 0;
        // alpha unchanged
    }
}

// Boots tinting: 4-sentinel direct swap on the base body sprite's shoe pixels.
// Mirrors FarmerRenderer.ApplyShoeColor — each shoe sentinel becomes the matching
// shade of the equipped boot's shoeColors row. The sentinels appear only on the
// boots, so the rest of the body is left untouched.
function applyBootColor(
    imageData: ImageData,
    bootColors: readonly (readonly [number, number, number])[],
): void {
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        for (let s = 0; s < 4; s++) {
            const sen = SHOE_SENTINELS[s];
            if (data[i] === sen[0] && data[i + 1] === sen[1] && data[i + 2] === sen[2]) {
                data[i]     = bootColors[s][0];
                data[i + 1] = bootColors[s][1];
                data[i + 2] = bootColors[s][2];
                break;
            }
        }
    }
}

// Offscreen canvas helper: draw img, apply tintFn in-place, return modified ImageData
function tintImageData(img: HTMLImageElement, tintFn: (data: ImageData) => void): ImageData {
    const oc = document.createElement('canvas');
    oc.width = img.naturalWidth;
    oc.height = img.naturalHeight;
    const ctx = oc.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0);
    const id = ctx.getImageData(0, 0, oc.width, oc.height);
    tintFn(id);
    return id;
}

// Read the 3 palette replacement RGB values for a given skin_index from skin-colors.png
// skin-colors.png is 3×24: row = skin_index, col 0 = dark, col 1 = mid, col 2 = light
function readSkinPalette(skinColorsImg: HTMLImageElement, skinIndex: number): [number, number, number][] {
    const oc = document.createElement('canvas');
    oc.width = 3;
    oc.height = skinColorsImg.naturalHeight;
    const ctx = oc.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(skinColorsImg, 0, 0);
    const id = ctx.getImageData(0, 0, 3, skinColorsImg.naturalHeight);
    const row = Math.min(Math.max(skinIndex, 0), (skinColorsImg.naturalHeight - 1));
    const result: [number, number, number][] = [];
    for (let col = 0; col < 3; col++) {
        const px = (row * 3 + col) * 4;
        result.push([id.data[px], id.data[px + 1], id.data[px + 2]]);
    }
    return result;
}

// ── Module-level skin-colors cache (shared across all farmer canvases) ─────────

let skinColorsCachePromise: Promise<HTMLImageElement> | null = null;

function getSkinColors(): Promise<HTMLImageElement> {
    if (!skinColorsCachePromise) {
        skinColorsCachePromise = loadImage(urlSkinColors());
    }
    return skinColorsCachePromise;
}

// ── Fallback silhouette ───────────────────────────────────────────────────────

function drawFallbackSilhouette(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    ctx.fillStyle = 'rgba(120, 120, 120, 0.5)';
    // Head — ellipse centred in upper portion of canvas
    ctx.beginPath();
    ctx.ellipse(w / 2, h * 0.18, w * 0.18, w * 0.22, 0, 0, Math.PI * 2);
    ctx.fill();
    // Body — rectangle below head
    ctx.fillRect(w * 0.28, h * 0.38, w * 0.44, h * 0.38);
}

// ── Convert ImageData to a stable offscreen canvas (called once at init, not per frame) ──

function imageDataToCanvas(imageData: ImageData): HTMLCanvasElement {
    const oc = document.createElement('canvas');
    oc.width = imageData.width;
    oc.height = imageData.height;
    const ctx = oc.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.putImageData(imageData, 0, 0);
    return oc;
}

// Draw a pre-baked offscreen canvas into the target context, scaled
function drawTintedFrame(
    targetCtx: CanvasRenderingContext2D,
    oc: HTMLCanvasElement,
    dx: number,
    dy: number,
    scale: number,
): void {
    targetCtx.imageSmoothingEnabled = false;
    targetCtx.drawImage(oc, dx, dy, oc.width * scale, oc.height * scale);
}

// ── Walk-animation loop ───────────────────────────────────────────────────────

interface StaticLayers {
    // Phase 78-03 patch: for dyeable shirts, `shirt` is the dye-tinted base
    // (left-half multiplied by shirt_color) as an offscreen canvas, not the raw
    // CDN image. Non-dyeable shirts pass through the raw image unchanged.
    shirt: HTMLImageElement | HTMLCanvasElement | null;
    accessory: HTMLImageElement | null;
    hat: HTMLImageElement | null;
    // Phase 78-03: pre-baked dyed shirt mask (multiplied by shirt_color at init).
    // Drawn directly on top of `shirt` at the same x/y. null when shirt is not
    // dyeable or the mask sprite failed to load.
    shirtDyedMask: HTMLCanvasElement | null;
}

function startWalkLoop(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    tintedBodyFrames: HTMLCanvasElement[],
    tintedArmFrames: HTMLCanvasElement[],
    tintedHair: HTMLCanvasElement,
    tintedPantsFrames: HTMLCanvasElement[] | null,
    staticLayers: StaticLayers,
    app: FarmerAppearance,
): void {
    let stepIndex = 0;
    let lastTimestamp = 0;
    let rafId = 0;

    function drawFrame(step: number): void {
        const bodyIdx  = WALK_CYCLE[step];
        const pantsIdx = tintedPantsFrames ? WALK_CYCLE[step] % tintedPantsFrames.length : 0;
        // SDV FarmerRenderer per-frame Y bob — applied to shirt/hat/accessory/hair.
        // featureYOffsetPerFrame is the canonical SDV table and encodes the head bob.
        // The prior HEAD_BOB shortcut is removed: hair now uses the canonical formula
        // (see hair draw block below) instead of HEAD_BOB.
        const featureY = FEATURE_Y_OFFSET_PER_FRAME[bodyIdx];

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Bottom-to-top draw order: body → pants → shirt → accessory → hair → hat → ARM
        // The near arm draws LAST (layer_offset 4.9E-05f in SDV FarmerRenderer.cs:1028,
        // strictly above the hat's 3.9E-05f at line 945). Drawing arm before hair caused
        // long-hair tails (e.g. TAI-SAUCE hair_index=26) to occlude the upper arm — fix
        // is to defer the arm draw to after the hat. See SDV FarmerRenderer.cs:1027-1034.

        // Body at canvas (0, BODY_Y) — walk frames already contain the head bob
        drawTintedFrame(ctx, tintedBodyFrames[bodyIdx], 0, BODY_Y, SCALE);

        // Pants overlay at same position as body (pants frames are walk frames).
        // Phase 78-02 audit: pants Y intentionally unchanged — body walk frames
        // encode the leg animation, no featureYOffsetPerFrame add for pants.
        if (tintedPantsFrames) {
            drawTintedFrame(ctx, tintedPantsFrames[pantsIdx], 0, BODY_Y, SCALE);
        }

        // Shirt: 8×8 source sprite on the torso — Y uses SDV FarmerRenderer formula:
        // shirt_y_src = SHIRT_Y_BASE_SRC(14) + featureYOffsetPerFrame[bodyFrame].
        // (Replaces the old SHIRT_Y_SRC=16 + HEAD_BOB shortcut, which rendered ~1
        // source px too low across all walk frames.)
        // Phase 78-03 Bug 3: for dyeable shirts, draw the colored half first (may
        // be transparent for pure-mask shirts like Fake Muscles), then the pre-baked
        // dyed mask on top — mirrors SDV FarmerRenderer dye compositing.
        if (staticLayers.shirt) {
            ctx.imageSmoothingEnabled = false;
            const sx = SHIRT_X_SRC * SCALE;
            const sy = BODY_Y + (SHIRT_Y_BASE_SRC + featureY) * SCALE;
            // HTMLImageElement uses naturalWidth/Height; HTMLCanvasElement uses width/height.
            const shirtEl = staticLayers.shirt;
            const sw = (shirtEl instanceof HTMLCanvasElement ? shirtEl.width : shirtEl.naturalWidth) * SCALE;
            const sh = (shirtEl instanceof HTMLCanvasElement ? shirtEl.height : shirtEl.naturalHeight) * SCALE;
            ctx.drawImage(staticLayers.shirt, sx, sy, sw, sh);
            if (staticLayers.shirtDyedMask) {
                ctx.drawImage(staticLayers.shirtDyedMask, sx, sy, sw, sh);
            }
        }

        // Accessory: 16×16, on the face/head — bobs with featureYOffsetPerFrame
        // (Phase 78-02: replaces the prior HEAD_BOB shortcut).
        if (app.accessory_index !== null && staticLayers.accessory) {
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(
                staticLayers.accessory,
                0,
                BODY_Y + featureY * SCALE,
                staticLayers.accessory.naturalWidth * SCALE,
                staticLayers.accessory.naturalHeight * SCALE,
            );
        }

        // Hair Y per SDV FarmerRenderer.cs:894 (down-facing, case 2):
        //   hair_y_dest = featureYOffsetPerFrame[bodyFrame] * 4
        //                 + ((IsMale && hair >= 16) ? -4 : (!IsMale && hair < 16) ? 4 : 0)
        // The gender-hair conditional accounts for SDV's gender-swapped hairstyle handling:
        // male hairs 16+ shift UP 1 src px, female hairs <16 shift DOWN 1 src px so cross-
        // gender hairs sit naturally on the head.
        //
        // Hair source-rect TOP CROP when a hat is equipped (pragmatic approximation —
        // applied ONLY to "tall" hairstyles whose dome extends above the hat brim line.
        //
        // BUG FIX (Phase 78-05): commit 22340ed applied the y=0..7 crop UNCONDITIONALLY
        // when any hat was equipped. For hairs whose topmost opaque pixel is at y >= 3
        // (most styles — including the user's mushroom-cap farmer), those y=0..7 pixels
        // ARE the hairline strands the hat brim should sit ON TOP of, not over. Cropping
        // them exposes the body's forehead skin where hair used to be → "light horizontal
        // bar below the hat".
        //
        // FIX: classify per-hairstyle via a one-time pixel inspection of hairstyles.png /
        // hairstyles2.png. Only crop hairs whose topmost opaque y < 3 (the dome sits in
        // the hat-brim zone). Hairs with top_y >= 3 already have a natural "hairline gap"
        // in the brim zone — drawing them in full leaves no skin showing.
        //
        // Findings (top_y per hair_index, down-facing frame):
        //   hairs with top_y < 3 (NEED CROP, 29 indices):
        //     0,1,2,3,4,6,7,8,9,10,11,12,13,14,15,16,19,21,22,35,37,38,40,42,49,60,62,68,70
        //   hair 52 has no opaque pixels (unused slot)
        //   all other indices have top_y >= 3 → no crop, full hair sprite renders
        //
        // For cropped hairs: source y=8..31 → dest y=BODY_Y+hairYDest+8*SCALE..+128.
        // Same dest math as before — only the gate is now per-hair instead of any-hat.
        //
        // When NO hat is equipped, full hair sprite renders unchanged (no crop).
        // Note: the `+ 4` baseline (= 1 src px down) is retained from commit 1e1b6fa —
        // empirically anchors hair to the body-sprite head top in our render. SDV's
        // case-2 source has no such constant, but our body sprite geometry differs
        // slightly (CDN-sliced single-frame body vs SDV's full sheet+positionOffset).
        const featureYDest = FEATURE_Y_OFFSET_PER_FRAME[bodyIdx] * SCALE;
        const hairGenderOffsetDest =
            (app.is_male && app.hair_index >= 16) ? -4 :
            (!app.is_male && app.hair_index < 16) ? 4 : 0;
        const hairYDest = featureYDest + 4 + hairGenderOffsetDest;
        if (app.hat_item_id && HAIRS_NEEDING_HAT_CROP.has(app.hair_index)) {
            // Hat present AND this hairstyle's dome extends into the brim zone:
            // crop top 8 src px from hair source rect, shift dest Y down by 8 src px so
            // the un-cropped portion stays anchored to its original canvas position.
            // Use raw drawImage (drawTintedFrame is whole-canvas only).
            const cropTopSrc = 8;
            ctx.imageSmoothingEnabled = false;
            const srcH = tintedHair.height - cropTopSrc;
            if (srcH > 0) {
                ctx.drawImage(
                    tintedHair,
                    0, cropTopSrc,                                   // src x, y
                    tintedHair.width, srcH,                          // src w, h
                    0, BODY_Y + hairYDest + cropTopSrc * SCALE,      // dest x, y
                    tintedHair.width * SCALE, srcH * SCALE,          // dest w, h
                );
            }
        } else {
            // No hat, OR hat with a short-dome hair (top_y >= 3): full hair sprite, no crop.
            // For short-dome hairs the natural hairline gap means the brim covers
            // nothing important and skin doesn't leak through.
            drawTintedFrame(ctx, tintedHair, 0, BODY_Y + hairYDest, SCALE);
        }

        // Hat: 20×20 — Y per SDV FarmerRenderer.cs:959:
        //   y_dest = -16 + featureYOffsetPerFrame*4 + hairstyleHatOffset[hair%16] + 4
        // The hairstyleHatOffset value is in DESTINATION pixels (not multiplied by 4),
        // so for our source-pixel math we divide by SCALE. The push (3-4 dest px ≈ 1
        // source px on hairs 3/6/8) shifts the hat DOWN slightly over taller hairstyles
        // so the brim sits cleanly on the tuft. SDV applies this to the HAT, not hair.
        if (staticLayers.hat) {
            const hairKey = ((app.hair_index % 16) + 16) % 16; // negative-safe modulo
            const hairHatPushDest = HAIRSTYLE_HAT_OFFSET_DEST_PX[hairKey]; // dest pixels
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(
                staticLayers.hat,
                HAT_X_SRC * SCALE,
                (BODY_Y_SRC + HAT_Y_BASE_SRC + featureY) * SCALE + hairHatPushDest,
                staticLayers.hat.naturalWidth * SCALE,
                staticLayers.hat.naturalHeight * SCALE,
            );
        }

        // Near arm — drawn LAST so it sits above hair + hat (SDV FarmerRenderer.cs:1028
        // gives arm layer_offset 4.9E-05f > hat 3.9E-05f). This prevents long-hair tails
        // from covering the upper arm and matches the canonical SDV walk silhouette.
        const armIdx = bodyIdx % tintedArmFrames.length;
        drawTintedFrame(ctx, tintedArmFrames[armIdx], 0, BODY_Y, SCALE);
    }

    function tick(timestamp: number): void {
        if (timestamp - lastTimestamp >= FRAME_MS) {
            stepIndex = (stepIndex + 1) % 4;
            lastTimestamp = timestamp;
            drawFrame(stepIndex);
        }
        rafId = requestAnimationFrame(tick);
    }

    // IntersectionObserver: start rAF when visible, pause when off-screen
    const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
            rafId = requestAnimationFrame(tick);
        } else {
            cancelAnimationFrame(rafId);
        }
    });
    observer.observe(canvas);

    // Draw frame 0 synchronously so off-screen avatars still show a static pose
    drawFrame(0);
}

// ── Per-canvas initialisation ─────────────────────────────────────────────────

async function initAvatar(canvas: HTMLCanvasElement, app: FarmerAppearance): Promise<void> {
    // Guarantee the drawing buffer is the correct size
    canvas.width  = 64;
    canvas.height = 136;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;

    const gender: 'male' | 'female' = app.is_male ? 'male' : 'female';
    const pantsIdx = pantsSpriteIndex(app.pants_item_id);

    // Build load lists
    const bodyUrls   = [0, 1, 2, 3].map(f => urlBodyFrame(gender, f));
    const armUrls    = [0, 1, 2].map(f => urlArmFrame(gender, f));
    const hairUrl    = urlHairFrame(app.hair_index, 0);   // down-facing only — avatar never turns
    const pantsUrls  = [0, 1, 2].map(f => urlPantsFrame(pantsIdx, f));
    const shirtSpr   = shirtSpriteIndex(app.shirt_item_id, app.shirt_index);
    const shirtUrl   = urlShirt(shirtSpr);
    // Phase 78-03: only load the mask if this shirt SpriteIndex is dyeable per Shirts.json.
    const shirtMaskUrl = SHIRT_DYEABLE.has(shirtSpr) ? urlShirtMask(shirtSpr) : null;
    const accUrl     = app.accessory_index !== null ? urlAccessory(app.accessory_index) : null;
    const hatUrl     = app.hat_item_id ? urlHat(app.hat_item_id) : null;

    // Load all layers in parallel; one 404 must not cancel the batch
    const bodySettled  = await Promise.allSettled(bodyUrls.map(loadImage));
    const armSettled   = await Promise.allSettled(armUrls.map(loadImage));
    const hairImg      = await loadImage(hairUrl).catch(() => null);
    const pantsSettled = await Promise.allSettled(pantsUrls.map(loadImage));
    const [shirtSettled, shirtMaskSettled, accSettled, hatSettled, skinColorsSettled] = await Promise.allSettled([
        loadImage(shirtUrl),
        shirtMaskUrl ? loadImage(shirtMaskUrl) : Promise.reject(new Error('no shirt mask')),
        accUrl ? loadImage(accUrl) : Promise.reject(new Error('no accessory')),
        hatUrl ? loadImage(hatUrl) : Promise.reject(new Error('no hat')),
        getSkinColors(),
    ]);

    // Extract resolved images
    const bodyFrames: (HTMLImageElement | null)[] = bodySettled.map(r => r.status === 'fulfilled' ? r.value : null);
    const armFrames: (HTMLImageElement | null)[] = armSettled.map(r => r.status === 'fulfilled' ? r.value : null);
    const pantsFrames: (HTMLImageElement | null)[] = pantsSettled.map(r => r.status === 'fulfilled' ? r.value : null);
    const shirtImg: HTMLImageElement | null = shirtSettled.status === 'fulfilled' ? shirtSettled.value : null;
    const shirtMaskImg: HTMLImageElement | null = shirtMaskSettled.status === 'fulfilled' ? shirtMaskSettled.value : null;
    const accImg: HTMLImageElement | null   = accSettled.status === 'fulfilled' ? accSettled.value : null;
    const hatImg: HTMLImageElement | null   = hatSettled.status === 'fulfilled' ? hatSettled.value : null;
    const skinColorsImg: HTMLImageElement | null = skinColorsSettled.status === 'fulfilled' ? skinColorsSettled.value : null;

    // If ALL body frames failed → fallback silhouette and bail
    if (bodyFrames.every(f => f === null)) {
        drawFallbackSilhouette(ctx, canvas.width, canvas.height);
        return;
    }

    // Determine skin palette — fall back to skin_index=0 sentinels if palette image failed
    let skinPalette: [number, number, number][];
    if (skinColorsImg) {
        skinPalette = readSkinPalette(skinColorsImg, app.skin_index) as [number, number, number][];
    } else {
        // Fallback: skin_index=0 values from research (unchanged body rendering)
        skinPalette = [[107, 0, 58], [224, 107, 101], [249, 174, 137]];
    }

    const { r: er, g: eg, b: eb } = app.eye_color;
    const { r: hr, g: hg, b: hb } = app.hair_color;
    const { r: pr, g: pg, b: pb } = app.pants_color;

    // Equipped boots recolor the shoe pixels of the base body sprite; null = none worn
    const bootColors = bootColorRow(app.boots_item_id);

    // Pre-tint body frames (skin swap + eye swap + boot swap) — ONCE, converted to stable offscreen canvases
    const tintedBodyFrames: HTMLCanvasElement[] = bodyFrames.map(img => {
        if (!img) {
            return imageDataToCanvas(new ImageData(16, 32));
        }
        const id = tintImageData(img, (d) => {
            applySkintone(d, SKIN_SENTINELS, skinPalette);
            applyEyeColor(d, er, eg, eb);
            if (bootColors) applyBootColor(d, bootColors);
        });
        return imageDataToCanvas(id);
    });

    // Pre-tint arm frames (skin swap only — the arm carries no eye pixels)
    const tintedArmFrames: HTMLCanvasElement[] = armFrames.map(img => {
        if (!img) return imageDataToCanvas(new ImageData(16, 32));
        return imageDataToCanvas(tintImageData(img, (d) => applySkintone(d, SKIN_SENTINELS, skinPalette)));
    });

    // Pre-tint hair (down-facing frame only) — greyscale multiply
    const tintedHair: HTMLCanvasElement = hairImg
        ? imageDataToCanvas(tintImageData(hairImg, (d) => applyHairColor(d, hr, hg, hb)))
        : imageDataToCanvas(new ImageData(16, 32));

    // Pre-tint pants frames (multiply blend); null if all failed
    const anyPantsLoaded = pantsFrames.some(f => f !== null);
    const tintedPantsFrames: HTMLCanvasElement[] | null = anyPantsLoaded
        ? pantsFrames.map(img => {
            if (!img) return imageDataToCanvas(new ImageData(16, 32));
            return imageDataToCanvas(tintImageData(img, (d) => applyPantsColor(d, pr, pg, pb)));
        })
        : null;

    // Phase 78-03 Bug 3: pre-bake the dyed shirt mask at init time (NOT in the
    // rAF callback — Phase 62 Pitfall 5). MultiplyColor each non-transparent
    // mask pixel by the farmer's shirt_color (clothesColor RGB from save XML).
    //
    // Phase 78-03 PATCH (post-verify): for dyeable shirts we ALSO multiply the
    // base (left-half) by shirt_color, mirroring SDV `Clothing.drawInMenu` —
    // both the colored half and the mask half receive the clothesColor tint
    // (Utility.MultiplyColor) when the shirt is dyeable. Without this, shirts
    // like High-Waisted (174) keep their authoring placeholder pink/blue and
    // render pink-not-red. Non-dyeable shirts pass through untouched.
    let shirtDyedMask: HTMLCanvasElement | null = null;
    let shirtForRender: HTMLImageElement | HTMLCanvasElement | null = shirtImg;
    if (shirtMaskImg && shirtImg) {
        const { r, g, b } = app.shirt_color;
        const dyedId = tintImageData(shirtMaskImg, (d) => applyDyeMask(d, r, g, b));
        shirtDyedMask = imageDataToCanvas(dyedId);
        // Tint the base half too (no-op for pure-mask shirts where it's all transparent).
        const baseId = tintImageData(shirtImg, (d) => applyDyeMask(d, r, g, b));
        shirtForRender = imageDataToCanvas(baseId);
    }

    const staticLayers: StaticLayers = {
        shirt:     shirtForRender,
        accessory: accImg,
        hat:       hatImg,
        shirtDyedMask,
    };

    startWalkLoop(canvas, ctx, tintedBodyFrames, tintedArmFrames, tintedHair, tintedPantsFrames, staticLayers, app);
}

// Phase 81 (HEAD-01, HEAD-02): render full avatar to a detached offscreen canvas, then slice the head region (16src × 20src → 32×40 dest, 2× CSS scale). Per CONTEXT D-01 the compositor stays untouched; per D-02 no rAF runs because `off` is never inserted into DOM.
// Phase 93 (FARM-01): exported so stardew-map.ts can bootstrap headCanvasCache once per payload load (RESEARCH Pitfall 5 — do NOT call per drawMap tick).
export async function initHeadCanvas(canvas: HTMLCanvasElement, app: FarmerAppearance): Promise<void> {
    canvas.width = 32;
    canvas.height = 40;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;

    const off = document.createElement('canvas');
    off.width = 64;
    off.height = 136;

    await initAvatar(off, app);

    ctx.drawImage(off, 0, 0, 64, 80, 0, 0, 32, 40);
}

// ── Public export ─────────────────────────────────────────────────────────────

// Tint the dyeable pants/skirt sprite shown in the equipment panel slot.
// The raw CDN sprite is un-dyed; multiply it by the farmer's pants_color.
function initPantsSlots(): void {
    document.querySelectorAll<HTMLCanvasElement>('.sv-equip-pants-canvas').forEach(canvas => {
        const src = canvas.dataset.pantsSrc;
        const color = (canvas.dataset.pantsColor ?? '').split(',').map(Number);
        if (!src || color.length !== 3 || color.some(isNaN)) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        loadImage(src).then(img => {
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            ctx.imageSmoothingEnabled = false;
            ctx.putImageData(
                tintImageData(img, (d) => applyPantsColor(d, color[0], color[1], color[2])),
                0, 0,
            );
        }).catch(() => { /* leave the slot blank if the sprite fails to load */ });
    });
}

// Phase 78-03 Bug 3: tint the dyeable shirt sprite shown in the equipment panel
// slot. Mirrors initPantsSlots(): load base icon, optionally load dye mask,
// apply MultiplyColor multiply blend with the per-farmer shirt_color, composite
// (base then dyed mask) at 1:1 (32×32 — no SCALE multiplier on inventory icons).
function initShirtSlots(): void {
    document.querySelectorAll<HTMLCanvasElement>('.sv-equip-shirt-canvas').forEach(canvas => {
        const src      = canvas.dataset.shirtSrc;
        const maskSrc  = canvas.dataset.shirtMaskSrc;
        const dyeable  = canvas.dataset.shirtDyeable === '1';
        const color    = (canvas.dataset.shirtColor ?? '').split(',').map(Number);
        if (!src) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.imageSmoothingEnabled = false;

        loadImage(src).then(async (baseImg) => {
            canvas.width  = baseImg.naturalWidth;
            canvas.height = baseImg.naturalHeight;
            ctx.imageSmoothingEnabled = false;

            // Phase 78-03 PATCH (post-verify): mirror SDV `Clothing.drawInMenu` —
            // for dyeable shirts, MultiplyColor the BASE half by clothesColor
            // (not just the mask). Otherwise the placeholder authoring color
            // baked into the left half (e.g. pink top of High-Waisted Shirt 174)
            // bleeds through and the icon renders wrong (pink not red).
            // Non-dyeable shirts: draw raw base, no tint, no mask.
            if (dyeable && color.length === 3 && !color.some(isNaN)) {
                const dyedBaseId = tintImageData(baseImg, (d) => applyDyeMask(d, color[0], color[1], color[2]));
                ctx.putImageData(dyedBaseId, 0, 0);
                if (maskSrc) {
                    try {
                        const maskImg = await loadImage(maskSrc);
                        const dyedMaskId = tintImageData(maskImg, (d) => applyDyeMask(d, color[0], color[1], color[2]));
                        ctx.drawImage(imageDataToCanvas(dyedMaskId), 0, 0);
                    } catch {
                        // Mask load failure: tinted base already drawn, leave as-is.
                    }
                }
            } else {
                // Non-dyeable shirt OR missing color metadata: draw raw base.
                ctx.drawImage(baseImg, 0, 0);
            }
        }).catch(() => { /* leave the slot blank if base sprite fails to load */ });
    });
}

export function init(): void {
    initPantsSlots();
    initShirtSlots();
    const canvases = document.querySelectorAll<HTMLCanvasElement>('.sv-equip-canvas');
    canvases.forEach(canvas => {
        const rawJson = canvas.dataset.appearance;
        if (!rawJson) return;
        try {
            const app = JSON.parse(rawJson) as FarmerAppearance;
            initAvatar(canvas, app);
        } catch {
            // Skip silently on parse error or missing appearance data
        }
    });
    // Phase 81 (HEAD-01, HEAD-02): head-only canvases — global tooltip init in shared.ts:30-36 handles [data-bs-toggle="tooltip"] auto
    document.querySelectorAll<HTMLCanvasElement>('.sv-head-canvas').forEach(canvas => {
        const rawJson = canvas.dataset.appearance;
        if (!rawJson) return;
        try {
            const app = JSON.parse(rawJson) as FarmerAppearance;
            initHeadCanvas(canvas, app);
        } catch {
            // Skip silently on parse error or missing appearance data
        }
    });
}
